import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';

import { useReportPage } from 'shared/hooks';
import { theme } from 'theme';

const useStyles = makeStyles({
  legendText: {
    fontSize: 17,
    fontWeight: 500,
    lineHeight: '20px',
  },
});

const Legend = ({ chart, extraText }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box display="flex" justifyContent="space-between" marginTop={4} marginBottom={2}>
      <Box display="flex">
        {chart.props.data.datasets.map(dataset => (
          <Box display="flex" marginRight={4} key={dataset.label}>
            <Box
              width={40}
              height={20}
              borderRadius="5px"
              marginRight={1}
              style={{ backgroundColor: dataset.backgroundColor }}
            />
            <Typography variant="body2" className={classes.legendText}>
              {dataset.label}
            </Typography>
          </Box>
        ))}
      </Box>
      {extraText && (
        <Typography variant="body2" className={classes.legendText}>
          {t(extraText)}
        </Typography>
      )}
    </Box>
  );
};

Legend.defaultProps = {
  extraText: undefined,
};

Legend.propTypes = {
  chart: PropTypes.object.isRequired,
  extraText: PropTypes.string,
};

const BarChart = ({ labels, datasets, showLegend, extraLegendText, unit }) => {
  const options = {
    scales: {
      xAxes: [
        {
          ticks: {
            fontSize: useReportPage ? 9 : 14,
            fontColor: theme.palette.custom.black,
            fontStyle: useReportPage ? 'normal' : 'bold',
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            fontSize: useReportPage ? 9 : 14,
            fontColor: theme.palette.custom.black,
            fontStyle: useReportPage ? 'normal' : 'bold',
          },
        },
      ],
    },
    legend: {
      display: false,
    },
    tooltips: {
      callbacks: {
        label: (tooltipItems, data) => {
          return `${data.datasets[tooltipItems.datasetIndex].label}: ${tooltipItems.yLabel}${unit ||
            ''}`;
        },
      },
    },
  };

  const { t } = useTranslation();
  const chartRef = useRef(null);

  const data = {
    labels: labels.map(label => t(label)),
    datasets,
  };

  const chart = chartRef.current;

  return (
    <Box>
      <Bar ref={chartRef} data={data} options={options} height={120} />
      {chart && showLegend && <Legend chart={chart} extraText={extraLegendText} />}
    </Box>
  );
};

BarChart.defaultProps = {
  showLegend: true,
  extraLegendText: undefined,
  unit: undefined,
};

BarChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.array.isRequired,
  showLegend: PropTypes.bool,
  extraLegendText: PropTypes.string,
  unit: PropTypes.string,
};

export default BarChart;
